import { withTimeout } from '@/composables/withTimeout'

export const AI_BUILDING_ENDPOINT = 'ai_building_status'
export const AI_BUILDING_POLL_INTERVAL_MS = 5000
export const AI_BUILDING_REQUEST_TIMEOUT_MS = 10000
// Backstop only. The server is the authority on liveness - it stops reporting a
// backfill once Onboarding::AiBuildingStatus::STALE_AFTER passes - so this sits
// deliberately beyond that. A client cap shorter than the server's would quit on
// a build that is still legitimately running.
export const AI_BUILDING_MAX_POLL_MS = 50 * 60 * 1000

function platformMap(status) {
  const platforms = status && status.platforms
  return platforms && typeof platforms === 'object' && !Array.isArray(platforms) ? platforms : {}
}

export function buildingPlatforms(status) {
  const platforms = platformMap(status)
  return Object.keys(platforms).filter(key => platforms[key] && platforms[key].ai_building)
}

export function isBuilding(status) {
  return buildingPlatforms(status).length > 0
}

// Resolves null rather than throwing. api.call turns 401/403 into navigation and
// this runs every few seconds in the background, so a failed poll must be a
// no-op for the merchant rather than something that moves them off the page.
export async function fetchAiBuildingStatus(client) {
  try {
    const res = await withTimeout(
      client('GET', AI_BUILDING_ENDPOINT, {}, { receive: false }),
      AI_BUILDING_REQUEST_TIMEOUT_MS,
      AI_BUILDING_ENDPOINT
    )
    return (res && res.data) || null
  } catch {
    return null
  }
}

export function pollAiBuildingStatus(client, options = {}) {
  const intervalMs = options.intervalMs ?? AI_BUILDING_POLL_INTERVAL_MS
  const maxElapsedMs = options.maxElapsedMs ?? AI_BUILDING_MAX_POLL_MS
  const onStatus = options.onStatus ?? (() => {})
  const hidden = options.hidden ?? (() => typeof document !== 'undefined' && document.hidden)

  let cancelled = false
  let timer = null
  let settle = () => {}

  const promise = new Promise(resolve => {
    settle = resolve
    const startedAt = Date.now()

    // Self-scheduling rather than setInterval: api.call is a bare fetch with no
    // request dedupe, so a status call slower than the interval would stack.
    const tick = async () => {
      if (cancelled) return
      if (Date.now() - startedAt >= maxElapsedMs) return resolve({ status: null, reason: 'expired' })

      if (hidden()) {
        timer = setTimeout(tick, intervalMs)
        return
      }

      const status = await fetchAiBuildingStatus(client)
      if (cancelled) return

      if (status) {
        onStatus(status)
        if (!isBuilding(status)) return resolve({ status, reason: 'done' })
      }

      timer = setTimeout(tick, intervalMs)
    }
    tick()
  })

  const cancel = () => {
    if (cancelled) return
    cancelled = true
    if (timer) clearTimeout(timer)
    settle(null)
  }

  return { promise, cancel }
}
